import { useCallback, useEffect, useState } from 'react';

import { bindAll } from 'bind-event-listener';

import { useAISmartButtonActions } from '@atlassian/ai-smart-button-api';

/**
 * The state only resets when:
 * - Location Changes: User navigates to a different page
 * - Page Refresh: Browser refreshes the page (global variables reset)
 * What Doesn't Reset the State
 * - Component Re-renders: State persists
 * - Component Unmount/Remount: State persists (this is what you want)
 * - Button Disappears/Reappears: State persists
 */

let globalAcceptedNudges: string[] = [];
let globalDismissedNudges: string[] = [];

// Export for testing purposes
export const resetGlobalState = () => {
	globalAcceptedNudges = [];
	globalDismissedNudges = [];
};

export const useNudgeSessionState = () => {
	const { setNudgesRefreshed } = useAISmartButtonActions();

	const [location, setLocation] = useState<string | undefined>(
		Boolean(process.env.REACT_SSR) || window === undefined ? undefined : window.location.pathname,
	);
	const [acceptedNudges, setAcceptedNudges] = useState<string[]>(globalAcceptedNudges);
	const [dismissedNudges, setDismissedNudges] = useState<string[]>(globalDismissedNudges);

	// Helper functions
	const addAcceptedNudge = useCallback((id: string) => {
		setAcceptedNudges((prev) => {
			if (!prev.includes(id)) {
				const newNudges = [...prev, id];
				globalAcceptedNudges = newNudges;
				return newNudges;
			}
			return prev;
		});
	}, []);

	const addDismissedNudge = useCallback((id: string) => {
		setDismissedNudges((prev) => {
			if (!prev.includes(id)) {
				const newNudges = [...prev, id];
				globalDismissedNudges = newNudges;
				return newNudges;
			}
			return prev;
		});
	}, []);

	const clearState = useCallback(() => {
		setNudgesRefreshed([]);
		setAcceptedNudges([]);
		setDismissedNudges([]);
		globalAcceptedNudges = [];
		globalDismissedNudges = [];
	}, [setNudgesRefreshed]);

	const handleLocationChange = useCallback(() => {
		if (Boolean(process.env.REACT_SSR) || window === undefined) {
			return;
		}

		if (location !== window.location.pathname) {
			setLocation(window.location.pathname);
			clearState();
		}
	}, [clearState, location, setLocation]);

	useEffect(() => {
		if (Boolean(process.env.REACT_SSR) || window === undefined) {
			return;
		}

		const originalPushState = window.history.pushState;
		const originalReplaceState = window.history.replaceState;

		window.history.pushState = function (...args) {
			originalPushState.apply(this, args);
			handleLocationChange();
		};

		window.history.replaceState = function (...args) {
			originalReplaceState.apply(this, args);
			handleLocationChange();
		};

		const unbind = bindAll(window, [
			{
				type: 'popstate',
				listener: handleLocationChange,
			},
		]);

		return () => {
			if (Boolean(process.env.REACT_SSR) || window === undefined) {
				return;
			}

			unbind();
			window.history.pushState = originalPushState;
			window.history.replaceState = originalReplaceState;
		};
	}, [handleLocationChange]);

	return {
		acceptedNudges,
		dismissedNudges,
		addAcceptedNudge,
		addDismissedNudge,
		clearState,
	};
};
